/**
 * The web side of the driver's offline outbox (docs/offline.md, Phase 3 chunk 1): ops live in the user's IndexedDB
 * `hub-<userId>` / `outbox` table (shared with unsent chat messages; each consumer sees only its own kinds) and are
 * sent on reconnect, when the app comes back to the foreground, on every new op and every 30 s while any wait.
 */
import { useEffect } from 'react';
import { applyFleetSent, applyRunSent, configureRunOutbox, createRunOutbox, isFleetOp, runOutboxStore, type RunOutbox } from '@tradeoutlet/data';
import { fleetSentToast } from '@tradeoutlet/shared';
import { useUi } from './store';
import { connection, useDeviceOnline } from './connection';
import { queryClient } from './queryClient';
import { hubDbName, indexedDbStore } from './idbStore';
import { clockOutbox } from './clockOutbox';
import { registerOutbox } from './registerOutbox';
import { uuid } from './uuid';

export const runOutbox: RunOutbox = createRunOutbox({
  store: runOutboxStore(userId => indexedDbStore(hubDbName(userId))),
  uuid,
  isOnline: () => connection.isDeviceOnline(), // t574u: navigator.onLine, unless the API has answered since
  onSent: (op, view, result) => {
    if (!isFleetOp(op.kind)) { applyRunSent(queryClient, op, view); return; }
    // a vehicle check / defect report saved with no signal has gone: say so, with the issue it raised (none on a discard)
    applyFleetSent(queryClient, op);
    if (result) useUi.getState().toast(fleetSentToast(op.kind === 'fleet.check' ? 'check' : 'report', result), 'success');
  },
});
configureRunOutbox(runOutbox);

/** Mounted once for a signed-in user: load their waiting run ops and clock punches (t187u) and keep trying to send them. */
export function useRunOutboxSync(userId: string | null | undefined) {
  useEffect(() => {
    if (!userId) return;
    void runOutbox.load(userId);
    void clockOutbox.load(userId);
    void registerOutbox.load(userId);
    const flush = () => { void runOutbox.flush(); void clockOutbox.flush(); void registerOutbox.flush(); };
    const vis = () => { if (document.visibilityState === 'visible') flush(); };
    window.addEventListener('online', flush);
    document.addEventListener('visibilitychange', vis);
    const t = window.setInterval(() => { if (runOutbox.ops().length || clockOutbox.ops().length || registerOutbox.ops().length) flush(); }, 30_000);
    return () => { window.removeEventListener('online', flush); document.removeEventListener('visibilitychange', vis); window.clearInterval(t); };
  }, [userId]);
}

/** The browser's online flag, live (drives the offline banner). */
export function useOnline(): boolean {
  return useDeviceOnline();
}
